/**
 * Header Component
 *
 * Main application header with logo, search, notifications, theme toggle, and profile menu.
 *
 */

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../theme';
import { useAuth } from '../../pages/Auth';
import { Button } from '../Button';
import { Avatar } from '../Avatar';
import { Badge } from '../Badge';
import { LanguageSwitcher } from '../LanguageSwitcher';

export interface HeaderProps {
  onToggleSidebar?: () => void;
  onMobileSidebarToggle?: () => void;
  onNotificationClick?: () => void;
  onProfileAction?: (action: string) => void;
  user?: {
    name: string;
    email: string;
    role: string;
    avatar?: string;
  };
  notificationCount?: number;
  isAuthenticated?: boolean;
  className?: string;
}

export const Header: React.FC<HeaderProps> = ({
  onToggleSidebar,
  onMobileSidebarToggle,
  onNotificationClick,
  onProfileAction,
  user = {
    name: 'John Doe',
    email: 'john.doe@acmecorp.com',
    role: 'Organization Admin',
  },
  notificationCount = 0,
  isAuthenticated = true,
  className,
}) => {
  const { t } = useTranslation();
  const { isDarkMode, toggleTheme } = useTheme();
  const { user: authUser } = useAuth();
  const navigate = useNavigate();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);

  const currentUser = authUser
    ? {
        name: `${authUser.first_name} ${authUser.last_name}`,
        email: authUser.email,
        role: authUser.role,
        avatar: authUser.photo,
      }
    : user;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNotificationClick = () => {
    setIsNotificationsOpen(!isNotificationsOpen);
    onNotificationClick?.();
  };

  const handleProfileAction = (action: string) => {
    onProfileAction?.(action);
    setIsProfileOpen(false);
  };

  const profileItems = [
    {
      key: 'profile',
      label: t('profile.title'),
      icon: 'settings',
      onClick: () => handleProfileAction('profile'),
    },
    {
      key: 'organization',
      label: t('organization.title'),
      icon: 'business',
      onClick: () => handleProfileAction('organization'),
    },
    {
      key: 'divider',
      label: '',
      type: 'divider' as const,
    },
    {
      key: 'theme',
      label: isDarkMode ? t('common.lightMode') : t('common.darkMode'),
      icon: isDarkMode ? 'light_mode' : 'dark_mode',
      onClick: () => toggleTheme(),
      mobileOnly: true,
    },
    {
      key: 'language',
      label: t('common.language'),
      icon: 'language',
      onClick: () => {}, // Will be handled by LanguageSwitcher in dropdown
      mobileOnly: true,
    },
    {
      key: 'logout',
      label: t('auth.signOut'),
      icon: 'logout',
      onClick: () => navigate('/sign-out'),
    },
  ];

  return (
    <header
      className={`bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm ${className || ''}`}
    >
      <div className="flex items-center justify-between px-4 py-3">
        {/* Left Section */}
        <div className="flex items-center space-x-4">
          {/* Desktop Menu Toggle */}
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggleSidebar}
            className="hidden md:flex p-2 hover:bg-primary-50 dark:hover:bg-primary-900/20"
          >
            <span className="material-icons text-lg text-gray-600 dark:text-gray-300">menu</span>
          </Button>

          {/* Mobile Menu Toggle */}
          <Button
            variant="ghost"
            size="sm"
            onClick={onMobileSidebarToggle}
            className="md:hidden p-2 hover:bg-primary-50 dark:hover:bg-primary-900/20"
          >
            <span className="material-icons text-lg text-gray-600 dark:text-gray-300">menu</span>
          </Button>

          {/* Logo */}
          <div className="flex items-center">
            <h1 className="text-xl sm:text-2xl font-bold font-display leading-30 tracking-0.50 bg-gradient-to-r from-primary-500 to-secondary-500 dark:from-secondary-400 dark:to-secondary-400 bg-clip-text text-transparent">
              VoxAI
            </h1>
          </div>
        </div>

        {/* Right Section */}
        <div className="flex items-center space-x-1 sm:space-x-2">
          {/* Help/Documentation */}
          <a
            href="https://docs.novofon.ai"
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            aria-label="Hilfe & Dokumentation"
          >
            <span className="material-icons text-lg text-gray-600 dark:text-gray-300">
              help_outline
            </span>
          </a>

          {/* Notifications */}
          <div className="relative">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleNotificationClick}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 relative"
            >
              <span className="material-icons text-lg text-gray-600 dark:text-gray-300">
                notifications
              </span>
              {notificationCount > 0 && (
                <Badge variant="error" size="sm" className="absolute -top-1 -right-1">
                  {notificationCount}
                </Badge>
              )}
            </Button>
          </div>

          {/* Theme Toggle - Desktop only */}
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleTheme}
            className="hidden lg:flex p-2 hover:bg-primary-50 dark:hover:bg-primary-900/20"
          >
            <span className="material-icons text-lg text-gray-600 dark:text-gray-300">
              {isDarkMode ? 'dark_mode' : 'light_mode'}
            </span>
          </Button>

          {/* Language Switcher - Desktop only */}
          <div className="hidden lg:block">
            <LanguageSwitcher />
          </div>

          {isAuthenticated ? (
            /* Profile Menu */
            <div className="relative" ref={profileRef}>
              <button
                onClick={() => setIsProfileOpen(!isProfileOpen)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setIsProfileOpen(!isProfileOpen);
                  }
                }}
                className="cursor-pointer p-1 border-none bg-transparent"
                aria-label="Toggle profile menu"
              >
                <Avatar
                  size="sm"
                  src={currentUser.avatar}
                  alt={currentUser.name}
                  className="shadow-none"
                >
                  {currentUser.name
                    .split(' ')
                    .map((n: string) => n[0])
                    .join('')}
                </Avatar>
              </button>

              {/* Profile Dropdown */}
              {isProfileOpen && (
                <div className="absolute right-0 top-full mt-2 w-72 sm:w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                  {/* User Info Header */}
                  <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                    <div className="text-sm font-semibold leading-27 text-gray-900 dark:text-white">
                      {currentUser.name}
                    </div>
                    <div className="text-xs font-medium leading-27 text-primary-600 dark:text-primary-400">
                      {t('users.owner')}
                    </div>
                  </div>

                  {/* Menu Items */}
                  <div className="py-2">
                    {profileItems.map(item => {
                      if (item.type === 'divider') {
                        return (
                          <div
                            key={item.key}
                            className="border-t border-gray-200 dark:border-gray-700 my-2"
                          />
                        );
                      }

                      // Hide mobile-only items on desktop
                      if (item.mobileOnly) {
                        return (
                          <div key={item.key} className="lg:hidden">
                            {item.key === 'language' ? (
                              <div className="px-4 py-2">
                                <div className="transform scale-90 origin-left">
                                  <LanguageSwitcher />
                                </div>
                              </div>
                            ) : (
                              <button
                                onClick={item.onClick}
                                className="w-full flex items-center px-4 py-2 text-sm font-medium leading-27 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                              >
                                <span className="material-icons text-lg mr-3 text-gray-500 dark:text-gray-400">
                                  {item.icon}
                                </span>
                                {item.label}
                              </button>
                            )}
                          </div>
                        );
                      }

                      // Add divider before logout on mobile if there are mobile-only items above
                      if (item.key === 'logout') {
                        const hasMobileItems = profileItems.some(i => i.mobileOnly);
                        return (
                          <React.Fragment key={item.key}>
                            {hasMobileItems && (
                              <div className="lg:hidden border-t border-gray-200 dark:border-gray-700 my-2" />
                            )}
                            <button
                              onClick={item.onClick}
                              className="w-full flex items-center px-4 py-2 text-sm font-medium leading-27 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                            >
                              <span className="material-icons text-lg mr-3 text-gray-500 dark:text-gray-400">
                                {item.icon}
                              </span>
                              {item.label}
                            </button>
                          </React.Fragment>
                        );
                      }

                      return (
                        <button
                          key={item.key}
                          onClick={item.onClick}
                          className="w-full flex items-center px-4 py-2 text-sm font-medium leading-27 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                          <span className="material-icons text-lg mr-3 text-gray-500 dark:text-gray-400">
                            {item.icon}
                          </span>
                          {item.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Authentication Buttons */
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/login')}
                className="px-4 py-2"
              >
                {t('auth.signIn')}
              </Button>
              <Button
                variant="gradient-multi"
                size="sm"
                onClick={() => navigate('/register')}
                className="px-4 py-2"
              >
                {t('auth.register')}
              </Button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
